import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    LinearProgress,
    Chip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { If } from "../../../components/If";
import { useAuth } from "../../../contexts/contexts";
import { formatCurrencyToShow } from "../../../helpers/currency";
import { defaultToast } from "../../../helpers/toast/defaultToast";
import { api } from "../../../services/api";
import { CustomerAccount, CustomerAccountPlan, CustomersProps } from "../types";
import { Plan } from "./types";

export interface Props {
    currentCustomer: CustomersProps | undefined;
    onPlanAssigned?: (account: CustomerAccount) => void;
}

const emptyPlan: Plan = {
    id: 0,
    product_id: 0,
    description: "",
    standard_payment_price: 0,
    installment_standard: 0,
    recurring_payment_price: 0,
    installment_recurring: 0,
    subscription_standard_payment_price: 0,
    subscription_recurring_payment_price: 0,
    subscription_annual_payment_price: 0,
    period_in_months: 0,
    is_active: 0,
    deleted_at: null,
};

function mapAccountPlan(plan?: CustomerAccountPlan | null): Plan {
    if (!plan) {
        return emptyPlan;
    }

    return {
        id: plan.id ?? 0,
        product_id: plan.product_id || 0,
        description: plan.description || "",
        standard_payment_price: plan.standard_payment_price || 0,
        installment_standard: plan.installment_standard || 0,
        recurring_payment_price: plan.recurring_payment_price || 0,
        installment_recurring: plan.installment_recurring || 0,
        subscription_standard_payment_price:
            plan.subscription_standard_payment_price || 0,
        subscription_recurring_payment_price:
            plan.subscription_recurring_payment_price || 0,
        subscription_annual_payment_price:
            plan.subscription_annual_payment_price || 0,
        period_in_months: plan.period_in_months || 0,
        is_active: plan.is_active || 0,
        deleted_at: null,
    };
}

function PlanContent({ currentCustomer, onPlanAssigned }: Props) {
    const { hasPermissions } = useAuth();
    const [plans, setPlans] = useState<Plan[]>([]);
    const [isSavingPlan, setIsSavingPlan] = useState(false);
    const [planSelected, setPlanSelected] = useState<Plan>(
        mapAccountPlan(currentCustomer?.account?.plan)
    );

    function fetchPlans() {
        api.get("/plans/all").then((response) => {
            setPlans(response.data.response);
        });
    }

    function handleChangePlan(planId: number) {
        if (!currentCustomer?.id || !planId) {
            return;
        }

        const nextPlan = plans.find((plan) => plan.id === planId);
        if (nextPlan) {
            setPlanSelected(nextPlan);
        }

        setIsSavingPlan(true);
        api.patch(`/account/assign-plan/${currentCustomer.id}/${planId}`)
            .then((response) => {
                const account = response.data.response as CustomerAccount;
                if (account) {
                    onPlanAssigned?.(account);
                    if (account.plan) {
                        setPlanSelected(mapAccountPlan(account.plan));
                    }
                }
                defaultToast("success", "Plano atualizado com sucesso!");
            })
            .catch((error) => {
                setPlanSelected(mapAccountPlan(currentCustomer?.account?.plan));
                defaultToast(
                    "error",
                    error.response?.data?.message || "Erro ao atualizar plano!"
                );
            })
            .finally(() => {
                setIsSavingPlan(false);
            });
    }

    const urlProposalCustomer = currentCustomer?.proposal_id
        ? `${window.location.origin}/comercial/proposal/proposalDetails?id=${currentCustomer.proposal_id}`
        : "";

    useEffect(() => {
        fetchPlans();
    }, []);

    useEffect(() => {
        setPlanSelected(mapAccountPlan(currentCustomer?.account?.plan));
    }, [currentCustomer?.account?.plan?.id]);

    const hasPlanDetails = Boolean(planSelected.id);

    return (
        <div className="mainContainer">
            <div className="titlePageContainer">
                <h2>Plano do Cliente</h2>
            </div>
            <div>
                <h4 style={{ marginBottom: "10px" }}>Plano</h4>

                <If condition={hasPermissions("produtos.editar")}>
                    <FormControl>
                        <InputLabel id="plan_select">
                            Selecionar Plano
                        </InputLabel>
                        <Select
                            labelId="plan_select"
                            id="demo-simple-select"
                            value={planSelected.id || ""}
                            label="Selecionar Plano"
                            onChange={(e) => {
                                handleChangePlan(Number(e.target.value));
                            }}
                            size="small"
                            sx={{ width: "250px" }}
                            disabled={
                                plans.length === 0 ||
                                isSavingPlan ||
                                !currentCustomer?.id
                            }
                        >
                            {plans.map((plan: Plan) => {
                                return (
                                    <MenuItem key={plan.id} value={plan.id}>
                                        {plan.description}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        <If condition={plans.length === 0 || isSavingPlan}>
                            <LinearProgress />
                        </If>
                    </FormControl>
                </If>
            </div>

            {urlProposalCustomer && (
                <div style={{ marginBottom: "10px" }}>
                    <h4 style={{ marginBottom: "10px" }}>Link da proposta</h4>
                    <Link to={urlProposalCustomer} target="_blank">
                        {urlProposalCustomer}
                    </Link>
                </div>
            )}

            <Divider />

            <h4 style={{ marginBottom: "10px" }}>Detalhes do plano</h4>
            <Divider />
            {hasPlanDetails ? (
                <div>
                    <div className="contentFields">
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>
                                    {planSelected.description ||
                                        "Plano não identificado"}
                                </strong>
                            </p>
                        </div>
                        <div className="contentRows">
                            <p>
                                <Chip
                                    label={
                                        planSelected.is_active === 1
                                            ? "Plano ativo"
                                            : "Plano inativo"
                                    }
                                    color={
                                        planSelected.is_active === 1
                                            ? "success"
                                            : "error"
                                    }
                                    size="small"
                                />
                            </p>
                        </div>
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>
                                    Parcelas disponíveis recorrente:{" "}
                                </strong>
                                {planSelected.installment_recurring ||
                                    "Não informado"}
                            </p>
                        </div>
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>Parcelas disponíveis padrão: </strong>
                                {planSelected.installment_standard ||
                                    "Não informado"}
                            </p>
                        </div>
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>Período do plano: </strong>
                                {planSelected.period_in_months} meses
                            </p>
                        </div>
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>Valor da ativação recorrente: </strong>
                                {formatCurrencyToShow(
                                    planSelected.recurring_payment_price
                                ) || "Não informado"}
                            </p>
                        </div>
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>Valor da ativação padrão: </strong>R$
                                {formatCurrencyToShow(
                                    planSelected.standard_payment_price
                                ) || "Não informado"}
                            </p>
                        </div>
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>Valor assinatura recorrente: </strong>
                                {formatCurrencyToShow(
                                    planSelected.subscription_recurring_payment_price
                                ) || "Não informado"}
                            </p>
                        </div>
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>Valor assinatura padrão: </strong>
                                {formatCurrencyToShow(
                                    planSelected.subscription_standard_payment_price
                                ) || "Não informado"}
                            </p>
                        </div>
                        <div className="contentRows">
                            <p className="boldText">
                                <strong>Valor assinatura anual: </strong>
                                {formatCurrencyToShow(
                                    planSelected.subscription_annual_payment_price
                                ) || "Não informado"}
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <p>Selecione um plano para atribuir ao cliente</p>
            )}
        </div>
    );
}
export default PlanContent;
